import { FieldType } from "../../type-system/FieldType";
import {
  ShopifyAppKey,
  ShopifyFulfillmentHoldKey,
  ShopifyFulfillmentHoldToAppKey,
  ShopifyFulfillmentHoldToFulfillmentOrderKey,
  ShopifyFulfillmentHoldToShopKey,
  ShopifyFulfillmentOrderKey,
  ShopifyShopKey,
} from "./keys";
import { ShopifyApiVersion } from "./ShopifyApiVersion";
import { ShopifyDominion } from "./ShopifyDominion";
import type { ConnectionModelSpecVersion } from "./ShopifyMetadata";
import { buildConnectionSpec } from "./ShopifyMetadata";

const specs: ConnectionModelSpecVersion = {
  key: ShopifyFulfillmentHoldKey,
  "2025-04": {
    name: "Shopify Fulfillment Hold",
    fields: [
      {
        name: "ID",
        fieldType: FieldType.ID,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "id", shopifyFieldKey: "id" } },
      },
      {
        name: "Display Reason",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "displayReason", shopifyFieldKey: "displayReason" } },
      },
      {
        name: "Reason",
        fieldType: FieldType.Enum,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "reason", shopifyFieldKey: "reason", isShopifyEnum: true } },
        configuration: {
          allowOther: true,
          allowMultiple: false,
          options: [
            { name: "AWAITING_PAYMENT" },
            { name: "AWAITING_RETURN_ITEMS" },
            { name: "HIGH_RISK_OF_FRAUD" },
            { name: "INCORRECT_ADDRESS" },
            { name: "INVENTORY_OUT_OF_STOCK" },
            { name: "ONLINE_STORE_POST_PURCHASE_CROSS_SELL" },
            { name: "OTHER" },
            { name: "UNKNOWN_DELIVERY_DATE" },
          ],
        },
      },
      {
        name: "Reason Notes",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "reasonNotes", shopifyFieldKey: "reasonNotes" } },
      },
      {
        name: "Handle",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "handle", shopifyFieldKey: "handle" } },
      },
      {
        name: "Held By App",
        fieldType: FieldType.BelongsTo,
        key: ShopifyFulfillmentHoldToAppKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: { graphQLFieldKey: "heldByApp", shopifyFieldKey: "heldByApp", graphQLSelection: "id" },
        },
        configuration: {
          relatedModelKey: ShopifyAppKey,
        },
      },
      {
        name: "Held By Requesting App",
        fieldType: FieldType.Boolean,
        contributorConfiguration: {
          [ShopifyDominion.id]: { graphQLFieldKey: "heldByRequestingApp", shopifyFieldKey: "heldByRequestingApp" },
        },
      },
      {
        name: "Fulfillment Order",
        fieldType: FieldType.BelongsTo,
        key: ShopifyFulfillmentHoldToFulfillmentOrderKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "fulfillmentOrderId",
            skipGraphQLSelection: true,
            shopifyFieldKey: "fulfillmentOrderId",
          },
        },
        configuration: {
          relatedModelKey: ShopifyFulfillmentOrderKey,
        },
      },
      {
        name: "Shop",
        fieldType: FieldType.BelongsTo,
        key: ShopifyFulfillmentHoldToShopKey,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "shop_id" } },
        configuration: {
          relatedModelKey: ShopifyShopKey,
        },
      },
    ],
    // Synced as part of ShopifyFulfillmentOrder
    syncConfig: { type: "inherit" },
  },
};

export const ShopifyFulfillmentHold = ({
  apiVersion,
  frameworkVersionConstraint,
  webhookTopicsForModel,
}: {
  apiVersion: ShopifyApiVersion;
  frameworkVersionConstraint?: string;
  webhookTopicsForModel?: string[];
}) => buildConnectionSpec(specs, apiVersion, frameworkVersionConstraint, webhookTopicsForModel);
